import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Lightbulb, 
  Plus, 
  Trash2, 
  BookOpen, 
  Calendar,
  ChevronDown,
  ChevronUp,
  Loader2,
  Save
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'calentamiento', label: '🔥 Calentamiento', color: 'text-orange-400' },
  { value: 'tecnica', label: '🎯 Técnica', color: 'text-blue-400' },
  { value: 'nutricion', label: '🥗 Nutrición', color: 'text-green-400' },
  { value: 'recuperacion', label: '😴 Recuperación', color: 'text-purple-400' },
  { value: 'motivacion', label: '💪 Motivación', color: 'text-amber-400' },
  { value: 'general', label: '📚 General', color: 'text-gray-400' },
];

const DailyTipsPage = () => {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTip, setExpandedTip] = useState(null);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [newTip, setNewTip] = useState({
    title: '',
    content: '',
    category: 'general',
    references: ''
  });

  useEffect(() => {
    fetchTips();
  }, []);

  const fetchTips = async () => {
    try {
      const response = await axios.get(`${API}/admin/tips`);
      setTips(response.data);
    } catch (error) {
      console.error('Failed to fetch tips:', error);
      toast.error('Error al cargar las píldoras');
    } finally {
      setLoading(false);
    }
  };

  const createTip = async () => {
    if (!newTip.title || !newTip.content) {
      toast.error('Título y contenido son obligatorios');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/tips`, newTip);
      toast.success('Píldora creada correctamente');
      setNewTip({ title: '', content: '', category: 'general', references: '' });
      setShowNewDialog(false);
      fetchTips();
    } catch (error) {
      toast.error('Error al crear la píldora');
    } finally {
      setSaving(false);
    }
  };

  const deleteTip = async (tipId) => {
    if (!window.confirm('¿Eliminar esta píldora?')) return;
    
    try {
      await axios.delete(`${API}/admin/tips/${tipId}`);
      toast.success('Píldora eliminada');
      fetchTips();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[5];
  };

  // Calculate which tip shows today
  const getTodayTipIndex = () => {
    if (tips.length === 0) return -1;
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    return dayOfYear % tips.length;
  };

  const todayIndex = getTodayTipIndex();

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="daily-tips-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Lightbulb className="h-8 w-8 text-amber-400" />
              Píldoras del Día
            </h1>
            <p className="text-muted-foreground mt-1">
              Contenido educativo diario para tus clientes
            </p>
          </div>
          
          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700" data-testid="new-tip-button">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Píldora
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-400" />
                  Nueva Píldora del Día
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input
                    value={newTip.title}
                    onChange={(e) => setNewTip({...newTip, title: e.target.value})}
                    placeholder="Ej: ¿Por qué calentamiento dinámico antes de la fuerza?"
                    className="bg-zinc-900/50 border-zinc-800"
                    data-testid="tip-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.value}
                        onClick={() => setNewTip({...newTip, category: cat.value})}
                        className={`px-3 py-1 rounded-full text-sm transition-all ${
                          newTip.category === cat.value 
                            ? 'bg-amber-500/30 ring-2 ring-amber-500' 
                            : 'bg-zinc-800 hover:bg-zinc-700'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Contenido * (usa **texto** para negrita)</Label>
                  <Textarea
                    value={newTip.content}
                    onChange={(e) => setNewTip({...newTip, content: e.target.value})}
                    placeholder="Escribe el contenido de la píldora..."
                    className="bg-zinc-900/50 border-zinc-800 min-h-[300px] font-mono text-sm"
                    data-testid="tip-content-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: Usa **texto** para destacar en negrita, • para viñetas, y saltos de línea para separar párrafos.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Referencias / Bibliografía (opcional)</Label>
                  <Textarea
                    value={newTip.references}
                    onChange={(e) => setNewTip({...newTip, references: e.target.value})}
                    placeholder="• McArdle - Exercise Physiology&#10;• NSCA - Essentials of Strength Training"
                    className="bg-zinc-900/50 border-zinc-800 min-h-[100px]"
                    data-testid="tip-references-input"
                  />
                </div>
                
                <Button 
                  onClick={createTip} 
                  disabled={saving}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  data-testid="save-tip-button"
                >
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" /> Guardar Píldora</>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-amber-500/20">
                <Lightbulb className="h-6 w-6 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{tips.length}</p>
                <p className="text-sm text-muted-foreground">Píldoras creadas</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-500/20">
                <Calendar className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {tips.length > 0 ? Math.ceil(tips.length) : 0} días
                </p>
                <p className="text-sm text-muted-foreground">de contenido disponible</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-500/20">
                <BookOpen className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  #{todayIndex + 1}
                </p>
                <p className="text-sm text-muted-foreground">Píldora de hoy</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tips List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
          </div>
        ) : tips.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Lightbulb className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No hay píldoras todavía
              </h3>
              <p className="text-muted-foreground mb-4">
                Crea tu primera píldora educativa para tus clientes
              </p>
              <Button 
                onClick={() => setShowNewDialog(true)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Primera Píldora
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {tips.map((tip, index) => {
              const catInfo = getCategoryInfo(tip.category);
              const isToday = index === todayIndex;
              const isExpanded = expandedTip === tip.id;
              
              return (
                <Card 
                  key={tip.id} 
                  className={`bg-card border-border transition-all ${
                    isToday ? 'ring-2 ring-amber-500/50 bg-amber-500/5' : ''
                  }`}
                  data-testid={`tip-card-${index}`}
                >
                  <CardContent className="p-4">
                    <div 
                      className="flex items-start gap-4 cursor-pointer"
                      onClick={() => setExpandedTip(isExpanded ? null : tip.id)}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                        isToday ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-foreground'
                      }`}>
                        {index + 1}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${catInfo.color} bg-white/10`}>
                            {catInfo.label}
                          </span>
                          {isToday && (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-500 text-black font-medium">
                              HOY
                            </span>
                          )}
                        </div>
                        <h3 className="font-bold text-foreground">{tip.title}</h3>
                        {!isExpanded && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {tip.content.replace(/\*\*/g, '').substring(0, 150)}...
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTip(tip.id);
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-muted-foreground">
                          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                        </Button>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="mt-4 pl-14 space-y-4 animate-fade-in">
                        <div className="prose prose-invert prose-sm max-w-none border-l-2 border-amber-500/30 pl-4">
                          {tip.content.split('\n\n').map((paragraph, i) => (
                            <p key={i} className="text-sm text-foreground/90 mb-3">
                              {paragraph.split('**').map((part, j) => 
                                j % 2 === 1 ? <strong key={j} className="text-amber-300">{part}</strong> : part
                              )}
                            </p>
                          ))}
                        </div>
                        
                        {tip.references && (
                          <div className="pt-3 border-t border-border">
                            <p className="flex items-start gap-2 text-xs text-muted-foreground">
                              <BookOpen className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              <span className="whitespace-pre-line">{tip.references}</span>
                            </p>
                          </div>
                        )}
                        
                        <div className="text-xs text-muted-foreground">
                          Creado: {new Date(tip.created_at).toLocaleDateString('es-ES', { 
                            day: 'numeric', month: 'long', year: 'numeric' 
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default DailyTipsPage;
